import * as React from 'react';
import { cn } from '../../lib/utils';

/* ============================================================================
   Pecas do padrao visual novo (shadcn + Tailwind), vindas do redesign.

   Ficam num modulo unico de proposito: sao pequenas, andam juntas e a primeira
   tela a usar precisa das quatro. Quando passarem de ~5 telas, vale quebrar em
   arquivos - antes disso, um arquivo evita seis imports por tela.

   REGRAS que estas pecas materializam (do handoff de design):
     - superficie sempre neutra; cor so quando carrega significado;
     - todo numero em TABULAR e alinhado a direita, para a coluna virar regua
       vertical e o olho comparar sem ler. Era "mono tabular" ate 27/08/2026: a
       troca de FAMILIA punha duas tipografias na mesma linha (rotulo em Inter,
       numero em Cascadia Mono) e foi a reclamacao de "fonte errada em varios
       lugares". O alinhamento vinha de `tabular-nums`, nao da familia - a Inter tem
       algarismo de largura constante nesse modo. A mono ficou para IDENTIFICADOR
       que se compara caractere a caractere (pacote, senha, serial), onde ela evita
       confundir 0/O e 1/l;
     - altura de linha por token (--gps-row-h), entao densidade e uma troca de
       atributo no <html>, nao mudanca de componente;
     - status e ponto + rotulo em pill discreta, nao bloco saturado.

   Todas as cores saem dos tokens (theme/tokens.css) via classes do Tailwind
   mapeadas em tailwind.config.js - nenhuma cor literal aqui.
   ============================================================================ */

// ----------------------------------------------------------------------------
// Numero tabular, na fonte do sistema. Use em TODA celula numerica e id.
// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------
// Cabecalho das tabelas em grid.
//
// COR CHEIA DA PALETA, e nao um tom fraco dela. Primeira tentativa foi
// `bg-primary/10` + `text-primary`, e o resultado foi CINZA na tela: o primary
// padrao do sistema e um azul-marinho escuro (#1d3049), e 10% de um tom escuro
// sobre branco da um cinza levemente azulado - a cor da paleta simplesmente nao
// aparecia. Tom fraco so parece colorido quando a cor de origem e saturada.
//
// Com `bg-primary` cheio o cabecalho passa a ser a cor escolhida em Aparencia, seja
// qual for o preset, e o texto usa `primary-foreground` (o contraste que o proprio
// preset define). Como bonus, a faixa fica com peso de cabecalho de tabela em vez
// de "linha um pouco diferente".
//
// Uma constante e nao onze copias: este mesmo cabecalho aparece em nove telas e
// dois modais, e ja aconteceu de metade delas ficar para tras num ajuste de cor.
// ----------------------------------------------------------------------------

/** Faixa de cabecalho das tabelas (altura 38px, texto 11px). */
export const CABECALHO_TABELA =
  'grid h-[34px] items-center border-b border-primary bg-primary px-3 text-[11px] font-bold uppercase tracking-[0.07em] text-primary-foreground';

/** Versao compacta (altura 34px, texto 10.5px) - tabelas dentro de modal e de detalhe. */
export const CABECALHO_TABELA_COMPACTO =
  'grid h-[30px] items-center border-b border-primary bg-primary px-3 text-[10px] font-bold uppercase tracking-[0.07em] text-primary-foreground';

/** Minusculas e sem acento, para busca comparar "politica" com "Política".

    Vive aqui porque ja tem tres consumidores (historico, abas do dispositivo e
    o que vier depois) e a alternativa era a mesma funcao copiada em cada um -
    do tipo que um dia diverge sem ninguem perceber. */
export function semAcento(v: unknown): string {
  return String(v ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function Num({
  children,
  className,
  // `title` porque numero abreviado precisa do valor inteiro em algum lugar
  // alcancavel - hora sem a data, contagem em "1,2k", chave truncada.
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span className={cn('tabular-nums', className)} title={title}>
      {children}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Cartao de indicador.
//
// `delta` e a razao de existir: numero sem comparacao e decoracao, nao metrica.
// A barra usa `proporcao` (0..1) e some quando nao ha escala real a mostrar -
// barra sem escala mente sobre a grandeza.
// ----------------------------------------------------------------------------
const TOM = {
  info: 'text-info',
  ok: 'text-ok',
  danger: 'text-danger',
  // `warn` entrou com o cartao de usuarios PENDENTES: pendencia nao e erro
  // (danger) nem conclusao (ok) - e algo esperando alguem.
  warn: 'text-warn',
  neutro: 'text-muted-foreground',
} as const;

/* Linha de acento no INICIO do cartao.

   Substitui a tentativa anterior de tingir o cartao INTEIRO (fundo + borda no
   tom). Com seis cartoes lado a lado, seis fundos coloridos viram um mostrador de
   cores: a cor deixa de apontar e passa a preencher, e o numero - que e o dado -
   compete com o proprio cartao.

   Uma faixa fina no topo resolve o mesmo problema com muito menos tinta: marca a
   categoria e deixa o numero ser a coisa mais forte do cartao. O fundo acompanha a
   faixa num tom lavado (ver FUNDO_TOM), e ai a moldura cinza deixa de existir -
   onde ja ha cor, borda e contorno repetido.

   `overflow-hidden` no cartao e o que impede a faixa de vazar do canto arredondado. */
const FAIXA_TOM: Record<string, string> = {
  info: 'bg-info',
  ok: 'bg-ok',
  danger: 'bg-danger',
  warn: 'bg-warn',
  neutro: 'bg-line',
};

/* Fundo do cartao, no tom da faixa e bem lavado.
   (19/08/2026) Antes o cartao era `border border-line bg-surface`: tinha a faixa
   colorida no topo E uma moldura cinza em volta - dois contornos disputando o mesmo
   trabalho, e o cinza roubando a cor da faixa. Agora, onde ja existe cor, a cor faz
   as duas coisas: o fundo lavadissimo (9% a 14% de alpha, os tokens `-soft`) define
   o cartao, e a moldura cinza sai.

   Os `-soft` sao os MESMOS tokens de PillStatus e dos avisos - entao "verde fraco"
   quer dizer a mesma coisa em todo o sistema, e ja vem ajustado para o tema escuro
   (no escuro o soft parte de um tom claro, senao desapareceria no fundo preto). */
const FUNDO_TOM: Record<string, string> = {
  info: 'bg-info-soft',
  ok: 'bg-ok-soft',
  danger: 'bg-danger-soft',
  warn: 'bg-warn-soft',
  // Sem tom nao ha cor para lavar: mantem a superficie e a moldura de sempre.
  neutro: 'bg-surface border border-line',
};

export function CartaoIndicador({
  rotulo,
  valor,
  legenda,
  delta,
  proporcao,
  tom = 'neutro',
  icone,
  texto,
  href,
  emFaixa,
}: {
  rotulo: string;
  valor: React.ReactNode;
  legenda?: string;
  delta?: string;
  proporcao?: number;
  tom?: keyof typeof TOM;
  icone?: React.ReactNode;
  /**
   * `true` trata o valor como NOME e nao como MEDIDA.
   *
   * POR QUE EXISTE (29/09/2026, relatado: "tem texto cortado e muito grande").
   * A faixa do detalhe do dispositivo mistura os dois: "19", "100%" e "1,74 GB"
   * sao medidas; "GERENTE FABIO" e "_GERENTE_FABIO" sao nomes. Com o mesmo
   * `text-2xl font-extrabold`, o nome quebrava em duas linhas, empurrava o
   * cartao e ainda comia o espaco da legenda - que, por estar na MESMA linha do
   * valor, aparecia como "a que pert..." e "R...".
   *
   * No modo texto: 13px, duas linhas no maximo, e o valor inteiro no `title`.
   */
  texto?: boolean;
  /**
   * Endereco para onde o cartao leva. Com ele o cartao vira link, com seta e
   * foco visivel.
   *
   * Grupo e Politica eram as duas unicas informacoes da faixa que apontam para
   * outra tela, e nao eram clicaveis - o suporte pedia "na tela de detalhe nao
   * tem mais o link para a politica do aparelho".
   */
  href?: string;
  /**
   * `true` = este cartao e um SEGMENTO de uma faixa unica (ver PainelLista), e nao
   * um cartao solto.
   *
   * Nesse modo ele NAO desenha moldura, sombra, canto arredondado nem o fundo
   * tonalizado: quem faz isso e a faixa em volta. Desenhar de novo aqui daria
   * borda dupla nos encontros e um degrade de fundos dentro da mesma moldura.
   *
   * O numero tambem encolhe (text-xl no lugar de text-2xl) e a barra fica mais
   * fina: tres segmentos na largura de um notebook nao aguentam a mesma escala de
   * um cartao que ocupava um terco da tela sozinho.
   */
  emFaixa?: boolean;
}) {
  const cor = TOM[tom];

  /* Com `href` o cartao vira <a>: alvo de teclado, foco visivel e borda no
     hover. Sem ele continua <div>, exatamente como era. */
  const Raiz: any = href ? 'a' : 'div';
  const classeBase = cn(
    'relative overflow-hidden',
    emFaixa ? 'px-3 py-2' : cn('rounded-lg p-3 shadow-card', FUNDO_TOM[tom] || FUNDO_TOM.neutro),
    href && 'block border border-transparent no-underline outline-none hover:border-primary focus-visible:ring-2 focus-visible:ring-primary'
  );

  return (
    <Raiz className={classeBase} {...(href ? { href } : {})}>
      {/* Na faixa a listra de cor vira uma barra VERTICAL na esquerda do segmento:
          no topo ela colaria na do vizinho e leria como uma linha unica atravessando
          a faixa, sem dizer a qual numero pertence. */}
      <span
        className={cn(
          'absolute',
          emFaixa ? 'inset-y-0 left-0 w-[3px]' : 'inset-x-0 top-0 h-[3px]',
          FAIXA_TOM[tom] || FAIXA_TOM.neutro
        )}
        aria-hidden
      />
      <div className="flex items-center gap-2">
        {icone && <span className={cn('grid h-6 w-6 place-items-center rounded-md bg-soft', cor)}>{icone}</span>}
        <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">{rotulo}</span>
      </div>

      {/* A LEGENDA SAIU DA LINHA DO VALOR.

          Estava ao lado dele, com `truncate`, e valor longo comia o espaco -
          era a causa de "a que pert..." e "R..." na tela. Em linha propria ela
          nao disputa largura com nada, nos dois modos. */}
      {texto ? (
        <div
          className={cn('font-bold leading-snug text-ink', emFaixa ? 'mt-0.5 text-[12px]' : 'mt-1.5 text-[13px]', cor)}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
          title={typeof valor === 'string' ? valor : undefined}
        >
          {valor}
        </div>
      ) : (
        <div className={cn(emFaixa ? 'mt-0.5' : 'mt-2')}>
          <Num className={cn('font-extrabold leading-none text-ink', emFaixa ? 'text-xl' : 'text-2xl', cor)}>
            {valor}
          </Num>
        </div>
      )}

      {legenda && <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{legenda}</span>}

      {href && (
        <span aria-hidden className="absolute bottom-2 right-2.5 text-[12px] text-primary">
          →
        </span>
      )}

      {delta && <div className="mt-1 text-xs font-semibold text-muted-foreground">{delta}</div>}

      {typeof proporcao === 'number' && (
        <div className={cn('overflow-hidden rounded-full bg-soft', emFaixa ? 'mt-1.5 h-[2px]' : 'mt-3 h-1')}>
          <div
            className={cn('h-full rounded-full bg-current', cor)}
            style={{ width: `${Math.max(0, Math.min(1, proporcao)) * 100}%` }}
          />
        </div>
      )}
    </Raiz>
  );
}

// ----------------------------------------------------------------------------
// Status como ponto + rotulo. Sem bloco saturado: o status nao deve gritar mais
// alto que o dado da linha.
// ----------------------------------------------------------------------------
export function PillStatus({ ativo, rotuloAtivo = 'ATIVO', rotuloInativo = 'INATIVO' }: { ativo: boolean; rotuloAtivo?: string; rotuloInativo?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center gap-1.5 rounded-md px-2 text-[11px] font-bold tracking-wide',
        ativo ? 'bg-ok-soft text-ok' : 'bg-danger-soft text-danger'
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {ativo ? rotuloAtivo : rotuloInativo}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Avatar de iniciais. Substitui imagem que nao existe: 791 aplicativos nao tem
// icone no nosso banco, e um quadrado cinza vazio por linha e pior que a inicial.
// ----------------------------------------------------------------------------
export function AvatarIniciais({ nome, className }: { nome: string; className?: string }) {
  const iniciais = React.useMemo(
    () =>
      String(nome || '?')
        .replace(/[^A-Za-zÀ-ÿ0-9 ]/g, ' ')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0])
        .join('')
        .toUpperCase() || '?',
    [nome]
  );

  return (
    <span
      className={cn(
        'grid h-6 w-6 flex-none place-items-center rounded-md border border-line bg-soft text-[10px] font-extrabold text-muted-foreground',
        className
      )}
    >
      {iniciais}
    </span>
  );
}



// ----------------------------------------------------------------------------
// Botao "baixar Excel".
//
// Existe para que TODA lista do sistema tenha exportacao com o mesmo rotulo, o
// mesmo estado de carregando e a mesma regra de desabilitado - antes so as telas
// feitas com PainelLista tinham, e cada uma das outras teria inventado a sua.
//
// `linhas` e uma FUNCAO, nao um array: montar os objetos da planilha custa um
// map sobre a lista inteira, e nao ha razao para pagar isso em cada render de uma
// tela que talvez nunca exporte. Ela e chamada no clique.
//
// Desabilitado quando nao ha nada para exportar, com o motivo no `title` - botao
// que nao responde e nao diz por que parece defeito.
// ----------------------------------------------------------------------------
/**
 * Icone de planilha (folha com grade). Usado pelos dois botoes de exportar do
 * sistema - o do `PainelLista` e o `BotaoExportar` das telas sem painel.
 */
export function IconePlanilha() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8.5 12.5h7M8.5 15.5h7M12 12.5v5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export function BotaoExportar({
  linhas,
  nomeArquivo,
  rotulo = 'Excel',
  className,
  /** Trava o botao quando a lista na tela nao vale - ver ModalDetalheApp. */
  desabilitado,
}: {
  linhas: () => Array<Record<string, any>>;
  nomeArquivo: string;
  rotulo?: string;
  className?: string;
  desabilitado?: boolean;
}) {
  const [ocupado, setOcupado] = React.useState(false);
  const [erro, setErro] = React.useState(false);

  async function exportar() {
    setOcupado(true);
    setErro(false);
    try {
      const { baixarPlanilha } = await import('../../utils/planilha');
      const ok = await baixarPlanilha(linhas(), nomeArquivo);
      if (!ok) setErro(true);
    } catch {
      setErro(true);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <button
      type="button"
      onClick={exportar}
      disabled={ocupado || desabilitado}
      title={
        desabilitado
          ? 'A lista não pôde ser carregada - não há o que exportar'
          : erro
            ? 'Nada para exportar nesta lista'
            : `Baixa a lista em ${nomeArquivo}.xlsx`
      }
      className={cn(
        'inline-flex h-9 flex-none items-center gap-1.5 rounded-lg border border-line px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40',
        className
      )}
    >
      {/* MESMO icone do botao de exportar do PainelLista - antes aqui era a flecha
          ↓ (U+2193) e la nao havia icone nenhum, entao a mesma acao tinha duas
          aparencias dependendo da tela. Folha com grade dentro: a forma diz
          "planilha", a flecha dizia apenas "baixar algo". */}
      <IconePlanilha />
      {ocupado ? 'Gerando...' : rotulo}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Seletor de status ATIVO/INATIVO.
//
// POR QUE VIROU PECA UNICA: a mesma marcacao estava copiada em CINCO modais
// (grupo, usuario, aplicativo, APK, regional). Cinco copias significam cinco
// lugares para a regra divergir - e ela acabou de mudar.
//
// A COR AQUI E CONSEQUENCIA, nao decoracao: verde e vermelho dizem o que a
// escolha FAZ (fica disponivel / sai de circulacao), e esse e o unico campo do
// formulario cuja escolha tem efeito imediato sobre o que os outros usuarios
// enxergam. Antes a opcao marcada era um retangulo branco com sombra: dava para
// saber QUAL estava escolhida, nao o que ela significava.
//
// Nao dependo so da cor: a opcao escolhida tambem fica em negrito e ganha o
// ponto ao lado - quem nao distingue verde de vermelho continua vendo qual esta
// ativa. E o `aria-pressed` diz o estado para o leitor de tela, que nao le cor
// nenhuma.
// ----------------------------------------------------------------------------
/**
 * Normaliza status vindo do backend para o TEXTO que a tela usa.
 *
 * 🔴 DEFEITO QUE ISTO CORRIGE - "ativo em varias telas nao esta vindo carregado".
 *
 * A API nao e consistente no formato do status, e as duas formas chegam nas telas:
 *
 *   - as rotas /paginado mapeiam por `StatusCore[item.status]` e devolvem a STRING
 *     ('ATIVO', 'INATIVO'). E o que as listagens recebem;
 *   - as rotas /byId devolvem `{...entidade}` cru, com o NUMERO da coluna
 *     (0 = INATIVO, 1 = ATIVO, 2 = PERDIDO). E o que os modais de edicao recebem.
 *
 * Os modais faziam `setStatus(String(d.status ?? 'ATIVO'))`. Com numero, isso
 * guarda a string '1' - e `SeletorStatus` compara com 'ATIVO'/'INATIVO', entao
 * NENHUM dos dois botoes aparecia escolhido. Pior que feio: o Salvar mandava '1'
 * para um backend que faz `StatusCore['1']`, que e `undefined`.
 *
 * Aqui em um lugar so, porque o mesmo par de formatos aparece em cinco modais.
 */
export function statusTexto(valor: unknown, padrao = 'ATIVO'): string {
  if (valor === null || valor === undefined || valor === '') return padrao;
  if (typeof valor === 'boolean') return valor ? 'ATIVO' : 'INATIVO';

  const t = String(valor).trim().toUpperCase();
  if (t === 'ATIVO' || t === 'INATIVO' || t === 'PERDIDO' || t === 'PENDENTE') return t;
  if (t === '1') return 'ATIVO';
  if (t === '0') return 'INATIVO';
  if (t === '2') return 'PERDIDO';
  if (t === '3') return 'PENDENTE';
  return padrao;
}

export function SeletorStatus({
  valor,
  onChange,
  rotuloAtivo = 'ATIVO',
  rotuloInativo = 'INATIVO',
  desabilitado,
}: {
  valor: string;
  onChange: (valor: string) => void;
  rotuloAtivo?: string;
  rotuloInativo?: string;
  desabilitado?: boolean;
}) {
  const opcoes = [
    { valor: rotuloAtivo, escolhido: 'bg-ok-soft text-ok ring-1 ring-ok' },
    { valor: rotuloInativo, escolhido: 'bg-danger-soft text-danger ring-1 ring-danger' },
  ];

  return (
    <div className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface-2 p-1">
      {opcoes.map((o) => {
        /* `statusTexto` aqui tambem, e nao so no chamador: enquanto houver rota
           devolvendo numero, um modal novo que esqueca de normalizar volta a
           mostrar os dois botoes apagados - e isso e um defeito silencioso, o
           componente simplesmente nao acende nada. */
        const ativo = statusTexto(valor, '').toUpperCase() === o.valor.toUpperCase();
        return (
          <button
            key={o.valor}
            type="button"
            disabled={desabilitado}
            aria-pressed={ativo}
            onClick={() => onChange(o.valor)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] transition-colors disabled:opacity-40',
              ativo ? cn('font-bold', o.escolhido) : 'font-semibold text-muted-foreground hover:text-ink'
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', ativo ? 'bg-current' : 'bg-line')} />
            {o.valor}
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Filtro segmentado com contagem.
//
// Substitui select de status: com 3 a 5 opcoes, o segmentado mostra as contagens
// SEM abrir nada, e o usuario ve de imediato que existem 14 inativos.
// ----------------------------------------------------------------------------
export type OpcaoSegmento<T extends string | number> = { valor: T; rotulo: string; total?: number; tom?: keyof typeof TOM };

export function FiltroSegmentado<T extends string | number>({
  opcoes,
  valor,
  onChange,
}: {
  opcoes: Array<OpcaoSegmento<T>>;
  valor: T;
  onChange: (valor: T) => void;
}) {
  return (
    <>
      {/* Em TELA BAIXA (HD) a tira sai e entra o seletor compacto - ela ocupava
          quase metade da barra de filtros com quatro segmentos. As duas classes
          vem do bloco de densidade em theme/global.css. */}
      <div className="oculto-em-tela-baixa inline-flex items-center gap-1 rounded-lg border border-line bg-surface-2 p-1">
        {opcoes.map((o) => {
          const ativo = o.valor === valor;
          return (
            <button
              key={String(o.valor)}
              type="button"
              onClick={() => onChange(o.valor)}
              className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-semibold transition-colors',
                ativo ? 'bg-surface text-ink shadow-card' : 'text-muted-foreground hover:text-ink'
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', ativo ? 'bg-current' : 'bg-line')} />
              {o.rotulo}
              {typeof o.total === 'number' && (
                <Num
                  className={cn('rounded px-1 text-[11px]', ativo ? 'bg-soft text-muted-foreground' : 'text-faint')}
                >
                  {o.total.toLocaleString('pt-BR')}
                </Num>
              )}
            </button>
          );
        })}
      </div>

      <SegmentoCompacto opcoes={opcoes} valor={valor} onChange={onChange} />
    </>
  );
}

/* ----------------------------------------------------------------------------
   Seletor compacto do filtro - a forma do FiltroSegmentado em tela baixa.

   Mesma linguagem do filtro de regional: campo com aparencia de entrada, valor
   em vigor visivel, lista abaixo. Fecha ao clicar fora, Esc fecha, setas andam e
   Enter escolhe - lista sem teclado nao e seletor.

   A BUSCA SO APARECE COM MAIS DE 6 OPCOES, e nao sempre. O pedido foi "um select
   com pesquisa igual da regional", e a semelhanca que importa e a forma; digitar
   para filtrar entre "Todos, Ativos, Inativos, Pendentes" custa mais que ler as
   quatro. Nas regionais, com centenas, a busca e o que torna o campo usavel. Se um
   dia um filtro tiver muitas opcoes, ela aparece sozinha.
   ---------------------------------------------------------------------------- */
function SegmentoCompacto<T extends string | number>({
  opcoes,
  valor,
  onChange,
}: {
  opcoes: Array<OpcaoSegmento<T>>;
  valor: T;
  onChange: (valor: T) => void;
}) {
  const [aberto, setAberto] = React.useState(false);
  const [busca, setBusca] = React.useState('');
  const [foco, setFoco] = React.useState(0);
  const raiz = React.useRef<HTMLDivElement | null>(null);

  const comBusca = opcoes.length > 6;

  const visiveis = React.useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return opcoes;
    return opcoes.filter((o) => String(o.rotulo).toLowerCase().includes(t));
  }, [opcoes, busca]);

  React.useEffect(() => {
    if (!aberto) return undefined;
    const aoApertar = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', aoApertar);
    return () => document.removeEventListener('mousedown', aoApertar);
  }, [aberto]);

  const atual = opcoes.find((o) => o.valor === valor);

  function escolher(o: OpcaoSegmento<T>) {
    onChange(o.valor);
    setAberto(false);
    setBusca('');
  }

  function aoTeclar(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && aberto) {
      e.preventDefault();
      e.stopPropagation();
      setAberto(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!aberto) {
        setAberto(true);
        return;
      }
      const passo = e.key === 'ArrowDown' ? 1 : -1;
      setFoco((i) => {
        const p = i + passo;
        if (p < 0) return visiveis.length - 1;
        if (p >= visiveis.length) return 0;
        return p;
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!aberto) {
        setAberto(true);
        return;
      }
      if (visiveis.length) escolher(visiveis[Math.min(foco, visiveis.length - 1)]);
    }
  }

  return (
    <div ref={raiz} className="apenas-tela-baixa relative">
      <button
        type="button"
        role="combobox"
        aria-expanded={aberto}
        onClick={() => setAberto((a) => !a)}
        onKeyDown={aoTeclar}
        className="flex h-9 w-[190px] items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 text-left text-[12px] font-semibold text-ink outline-none focus:border-primary"
      >
        <span className="h-1.5 w-1.5 flex-none rounded-full bg-current" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{atual?.rotulo ?? 'Filtrar'}</span>
        {typeof atual?.total === 'number' && (
          <Num className="flex-none rounded bg-soft px-1 text-[11px] text-muted-foreground">
            {atual.total.toLocaleString('pt-BR')}
          </Num>
        )}
        <span className="flex-none text-[10px] text-faint" aria-hidden>
          ▼
        </span>
      </button>

      {aberto && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-40 mt-1 w-[220px] overflow-hidden rounded-lg border border-line bg-surface shadow-card"
        >
          {comBusca && (
            <input
              autoFocus
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={aoTeclar}
              placeholder="Buscar"
              className="h-8 w-full border-b border-line bg-surface px-2.5 text-[12px] outline-none placeholder:text-faint"
            />
          )}

          <div className="max-h-[240px] overflow-y-auto">
            {visiveis.map((o, i) => {
              const ativo = o.valor === valor;
              return (
                <button
                  key={String(o.valor)}
                  type="button"
                  role="option"
                  aria-selected={ativo}
                  onMouseEnter={() => setFoco(i)}
                  onMouseDown={(e) => {
                    // Sem isto o campo perde o foco antes do clique ser processado.
                    e.preventDefault();
                    escolher(o);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 border-b border-line px-2.5 py-1.5 text-left text-[12px] last:border-b-0',
                    i === foco && 'bg-surface-2',
                    ativo && 'font-bold text-ink'
                  )}
                >
                  <span className={cn('h-1.5 w-1.5 flex-none rounded-full', ativo ? 'bg-primary' : 'bg-line')} />
                  <span className="min-w-0 flex-1 truncate">{o.rotulo}</span>
                  {typeof o.total === 'number' && (
                    <Num className="flex-none text-[11px] text-faint">{o.total.toLocaleString('pt-BR')}</Num>
                  )}
                </button>
              );
            })}
            {!visiveis.length && <div className="px-2.5 py-3 text-center text-[12px] text-faint">Nada encontrado.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
